import React from "react";
import { useRouteMatch } from "react-router";
import PropTypes from "prop-types";

import { Menu } from "semantic-ui-react";

import "./settings.css";

import history from "data/history";
import useHub from "data/hub/useHub";

import { useAccess } from "components/Access";
import { FeatureFlag, useFeatures } from "components/FeatureFlag";

import AccountSettings from "./components/AccountSettings";
import HubSettings from "./components/HubSettings";
import PersonalSettings from "./components/PersonalSettings";
import ServiceTeamSettings from "./components/ServiceTeamSettings";
import { SharedResources } from "./components/SharedResources";
import TemplatesSettings from "./components/TemplatesSettings";

function SettingsPages({ onChange, page }) {
  const hasAccess = useAccess();
  const sharedResourcesFlag = useFeatures("shared-resources");

  const pages = [
    {
      name: "hub",
      title: "Hub Settings",
      access: () => {
        return true;
      },
    },
    {
      name: "personal",
      title: "Personal Settings",
      access: () => {
        return true;
      },
    },
    {
      name: "account",
      title: "Account Settings",
      access: () => {
        return true;
      },
    },
    {
      name: "service-team",
      title: "Service Teams",
      featureFlag: "service-team-settings",
      access: () => {
        return true;
      },
    },
    {
      name: "templates",
      title: "Published Templates",
      featureFlag: "templates-marketplace",
      access: () => {
        return true;
      },
    },
    {
      name: "shared-resources",
      title: sharedResourcesFlag?.settings?.navTitle || "Shared Resources",
      access: () => {
        return hasAccess(["Administrator"]);
      },
    },
  ];

  return (
    <div className="settings-container">
      <div style={{ paddingTop: "2em", width: "340px" }}>
        <Menu
          floated="right"
          secondary
          size="huge"
          vertical
          data-test="settings-menu"
        >
          {pages.map(({ name, title, featureFlag, access }) => {
            return (
              access() && (
                <FeatureFlag
                  key={name}
                  id={featureFlag || "no-flag"}
                  enableOnMissing
                >
                  <Menu.Item
                    style={{ textAlign: "right" }}
                    active={page === name}
                    onClick={() => {
                      onChange(name);
                    }}
                  >
                    <span
                      className={
                        page === name
                          ? "active-header-font"
                          : "inactive-header-font"
                      }
                    >
                      {title}
                    </span>
                  </Menu.Item>
                </FeatureFlag>
              )
            );
          })}
        </Menu>
      </div>
      <div className="settings-details">
        {page === "service-team" && (
          <div style={{ marginLeft: "3em", width: "calc(95vw - 350px)" }}>
            <ServiceTeamSettings />
          </div>
        )}
        {page === "templates" && (
          <div style={{ marginLeft: "3em", maxWidth: "750px", width: "90%" }}>
            <TemplatesSettings />
          </div>
        )}
        {["account", "hub", "personal"].includes(page) && (
          <div style={{ marginLeft: "3em", maxWidth: "600px", width: "90%" }}>
            {page === "account" && <AccountSettings />}
            {page === "hub" && <HubSettings />}
            {page === "personal" && <PersonalSettings />}
          </div>
        )}
        {hasAccess(["Administrator"]) && page === "shared-resources" && (
          <div style={{ marginLeft: "3em", width: "calc(95vw - 350px)" }}>
            <SharedResources />
          </div>
        )}
      </div>
    </div>
  );
}

SettingsPages.propTypes = {
  page: PropTypes.oneOf([
    "account",
    "hub",
    "personal",
    "service-team",
    "templates",
    "shared-resources",
  ]),
  onChange: PropTypes.func.isRequired,
};

SettingsPages.defaultProps = {
  page: "hub",
};

function Settings() {
  const { hubId } = useHub();
  const { url, params } = useRouteMatch();

  const goTo = (location) => {
    const path = `/hub/${hubId}/settings/${location}`;
    if (path !== url) history.push(path);
  };

  if (!params?.settingsPage) goTo("hub");

  return <SettingsPages onChange={goTo} page={params.settingsPage} />;
}

export default Settings;
